import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import {
    DEFAULT_FACILITY_SESSION_TIMEOUT_MINUTES,
    getFacility,
    getFacilitySecuritySettings,
    updateFacilitySecuritySettings,
    updateFacilitySettings as updateFacilityProfile,
} from '@/services/facility.service';

interface FacilitySettings {
    sessionTimeoutMinutes: number;
    facilityName: string;
    facilityType: string | null;
    timezone: string;
}

type FacilitySettingsSaveOperation = {
    attempted: boolean;
    succeeded: boolean;
    errorMessage: string | null;
};

export interface FacilitySettingsSaveResult {
    profile: FacilitySettingsSaveOperation;
    security: FacilitySettingsSaveOperation;
    hasSuccess: boolean;
    hasFailure: boolean;
}

interface FacilityContextValue {
    facilityId: string | null;
    settings: FacilitySettings;
    isLoadingFacility: boolean;
    /** Persist facility settings to Supabase and return explicit per-section status. */
    updateFacilitySettings: (updates: Partial<Pick<FacilitySettings, 'facilityName' | 'facilityType' | 'sessionTimeoutMinutes'>>) => Promise<FacilitySettingsSaveResult>;
}

const DEFAULT_SETTINGS: FacilitySettings = {
    sessionTimeoutMinutes: DEFAULT_FACILITY_SESSION_TIMEOUT_MINUTES,
    facilityName: 'Einrichtung',
    facilityType: null,
    timezone: 'Europe/Berlin',
};

function getErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }

    if (typeof error === 'string' && error.trim()) {
        return error;
    }

    return 'Unbekannter Fehler';
}

const FacilityContext = createContext<FacilityContextValue | null>(null);

export function FacilityProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const facilityId = user?.facilityId ?? null;
    const [settings, setSettings] = useState<FacilitySettings>(DEFAULT_SETTINGS);
    const [isLoadingFacility, setIsLoadingFacility] = useState(false);

    useEffect(() => {
        if (!facilityId) {
            setSettings(DEFAULT_SETTINGS);
            return;
        }

        setIsLoadingFacility(true);

        Promise.allSettled([
            getFacility(facilityId),
            getFacilitySecuritySettings(facilityId),
        ])
            .then(([facilityResult, securityResult]) => {
                const facility = facilityResult.status === 'fulfilled' ? facilityResult.value : null;
                const security = securityResult.status === 'fulfilled' ? securityResult.value : null;

                if (facilityResult.status === 'rejected') {
                    logger.warn('[FacilityContext] Facility-Laden fehlgeschlagen:', facilityResult.reason);
                }

                if (securityResult.status === 'rejected') {
                    logger.warn('[FacilityContext] Security-Laden fehlgeschlagen:', securityResult.reason);
                }

                setSettings({
                    sessionTimeoutMinutes: security?.sessionTimeoutMinutes ?? DEFAULT_SETTINGS.sessionTimeoutMinutes,
                    facilityName: facility?.name || DEFAULT_SETTINGS.facilityName,
                    facilityType: facility?.facilityType || null,
                    timezone: facility?.timezone || DEFAULT_SETTINGS.timezone,
                });
            })
            .finally(() => {
                setIsLoadingFacility(false);
            });
    }, [facilityId]);

    const updateFacilitySettings = async (
        updates: Partial<Pick<FacilitySettings, 'facilityName' | 'facilityType' | 'sessionTimeoutMinutes'>>,
    ): Promise<FacilitySettingsSaveResult> => {
        if (!facilityId) {
            logger.warn('[FacilityContext] Kein facilityId — Update abgebrochen');

            return {
                profile: {
                    attempted: updates.facilityName !== undefined || updates.facilityType !== undefined,
                    succeeded: false,
                    errorMessage: 'Keine Einrichtung ausgewählt.',
                },
                security: {
                    attempted: updates.sessionTimeoutMinutes !== undefined,
                    succeeded: false,
                    errorMessage: 'Keine Einrichtung ausgewählt.',
                },
                hasSuccess: false,
                hasFailure: true,
            };
        }

        const hasFacilityProfileUpdates = updates.facilityName !== undefined || updates.facilityType !== undefined;
        const hasSecurityUpdates = updates.sessionTimeoutMinutes !== undefined;

        if (!hasFacilityProfileUpdates && !hasSecurityUpdates) {
            return {
                profile: { attempted: false, succeeded: true, errorMessage: null },
                security: { attempted: false, succeeded: true, errorMessage: null },
                hasSuccess: true,
                hasFailure: false,
            };
        }

        const nextSettingsPatch: Partial<FacilitySettings> = {};
        const result: FacilitySettingsSaveResult = {
            profile: {
                attempted: hasFacilityProfileUpdates,
                succeeded: false,
                errorMessage: null,
            },
            security: {
                attempted: hasSecurityUpdates,
                succeeded: false,
                errorMessage: null,
            },
            hasSuccess: false,
            hasFailure: false,
        };

        if (hasFacilityProfileUpdates) {
            try {
                await updateFacilityProfile(facilityId, {
                    name: updates.facilityName,
                    facilityType: updates.facilityType,
                });

                result.profile.succeeded = true;

                if (updates.facilityName !== undefined) {
                    nextSettingsPatch.facilityName = updates.facilityName;
                }

                if (updates.facilityType !== undefined) {
                    nextSettingsPatch.facilityType = updates.facilityType;
                }
            } catch (error) {
                logger.warn('[FacilityContext] Profil-Update fehlgeschlagen:', error);
                result.profile.errorMessage = getErrorMessage(error);
            }
        }

        if (hasSecurityUpdates) {
            try {
                const securitySettings = await updateFacilitySecuritySettings(facilityId, {
                    sessionTimeoutMinutes: updates.sessionTimeoutMinutes,
                });

                result.security.succeeded = true;
                nextSettingsPatch.sessionTimeoutMinutes = securitySettings.sessionTimeoutMinutes;
            } catch (error) {
                logger.warn('[FacilityContext] Security-Update fehlgeschlagen:', error);
                result.security.errorMessage = getErrorMessage(error);
            }
        }

        if (Object.keys(nextSettingsPatch).length > 0) {
            setSettings(prev => ({
                ...prev,
                ...nextSettingsPatch,
            }));
        }

        result.hasSuccess = result.profile.succeeded || result.security.succeeded;
        result.hasFailure =
            (result.profile.attempted && !result.profile.succeeded)
            || (result.security.attempted && !result.security.succeeded);

        return result;
    };

    return (
        <FacilityContext.Provider value={{ facilityId, settings, isLoadingFacility, updateFacilitySettings }}>
            {children}
        </FacilityContext.Provider>
    );
}

export function useFacility(): FacilityContextValue {
    const ctx = useContext(FacilityContext);
    if (!ctx) throw new Error('useFacility muss innerhalb von FacilityProvider verwendet werden');
    return ctx;
}
